import { useQuery } from "@tanstack/react-query";
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { Card, LoadingSpinner, ErrorMessage, PageHeader, AllergenChip } from "@/components/ui";
import { allergenService } from "@/services/allergen.service";

/**
 * Page de consultation du référentiel des allergènes (UE)
 * Lecture seule — les 14 allergènes obligatoires
 */
export function AllergensPage(): JSX.Element {
  const { data: allergens = [], isLoading, error: queryError } = useQuery({
    queryKey: ["allergens"],
    queryFn: () => allergenService.getAll(),
  });

  if (isLoading) {
    return <LoadingSpinner message="Chargement des allergènes..." />;
  }

  if (queryError) {
    return (
      <ErrorMessage
        message="Erreur lors du chargement des allergènes."
        onRetry={() => window.location.reload()}
        retryLabel="Réessayer"
      />
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Allergènes"
        subtitle="Référentiel des allergènes (règlement UE + champignons). Utilisé pour les ingrédients et les produits."
      />

      {allergens.length === 0 ? (
        <Card sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">Aucun allergène en base. Exécutez le seed du backend.</Typography>
        </Card>
      ) : (
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Icône</TableCell>
                  <TableCell>Nom</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allergens.map((allergen) => (
                  <TableRow key={allergen.id} hover>
                    <TableCell>
                      <AllergenChip label={allergen.code} />
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={{ fontSize: "1.25rem" }}>
                        {allergen.icon ?? "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {allergen.name}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
            <Typography variant="body2" color="text.secondary">
              {allergens.length} allergène{allergens.length > 1 ? "s" : ""} (référentiel UE)
            </Typography>
          </Box>
        </Card>
      )}
    </Box>
  );
}
